import React, { useEffect, useMemo, useState, lazy, useCallback, Suspense } from 'react';
import styles from './index.module.css';
import Header from './Header';
import PublicMessage from './PublicMessage';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import { useTheme } from '../../pages/home/lifehub/wechat/pages/utils/ThemeContext';

// 动态导入所有背景组件
const DarkClouds = lazy(() => import('../Animation/DarkClouds'));
const WaterWave = lazy(() => import('../Animation/WaterWave'));
const NauticalBackground = lazy(() => import('../Animation/NauticalBackground'));
const FlowerScene = lazy(() => import('../Animation/FlowerScene'));
const SakuraBackground = lazy(() => import('../Animation/SakuraBackground'));
const DetailsHomeBackground = lazy(() => import('../Animation/DetailsHomeBackground'));
const CustomBackground = lazy(() => import('../Animation/CustomBackground'));
const CandleAnimation = lazy(() => import('../Animation/CandleAnimation'));
const CompassTime = lazy(() => import('../Animation/CompassTime'));
const BallLoading = lazy(() => import('../Animation/BallLoading'));

const Player = lazy(() => import('../../pages/home/lifehub/music/pages/Player'));

// 背景组件映射
const backgroundComponents = {
  WaterWave: <WaterWave />,
  NauticalBackground: <NauticalBackground />,
  FlowerScene: <FlowerScene />,
  DarkClouds: <DarkClouds />,
  SakuraBackground: <SakuraBackground />,
  DetailsHomeBackground: <DetailsHomeBackground />,
  CandleAnimation: <CandleAnimation />,
  CompassTime: <CompassTime />,
  BallLoading: <BallLoading />,
  CustomBackground: <CustomBackground />,
};

function ModuleLayout({
  title = 'ChenBaby',
  background = null,
  menuItems = [],
  activeKey = '',
  tabs = null,
  hasPlayer = false,
  player = null,
  hidePublicMessage = false,
  hideTabs = false,
  hideHeader = false,
  hideSidebar = false,
  hideBottomNav = false,
  fullscreenContent = false,
  headerActions = null,
  onMenuClick,
  sidebarVisible = true,
  bottomNavVisible = true,
  children,
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showPublicMessage, setShowPublicMessage] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 1024);
  
  // 获取主题配置
  const { activeTheme, loading: themeLoading } = useTheme();

  // 动态背景组件（从主题配置中获取）
  const DynamicBackground = useMemo(() => {
    // 如果外部传入了 background，优先使用外部传入的
    if (background) {
      return background;
    }
    
    // 如果主题还在加载中，显示默认背景
    if (themeLoading && !activeTheme) {
      return <BallLoading />;
    }
    
    // 从主题配置中获取动画名称，默认为 BallLoading
    const animationName = activeTheme?.background_animation || 'BallLoading';
    return backgroundComponents[animationName] || <BallLoading />;
  }, [activeTheme, themeLoading, background]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // 判断是否显示播放器
  const shouldShowPlayer = hasPlayer || title === '音乐';

  const layoutClassName = useMemo(() => {
    return [
      styles.unifiedLayout,
      isMobile ? styles.isMobile : styles.isDesktop,
      sidebarCollapsed ? styles.sidebarCollapsed : '',
      shouldShowPlayer ? styles.hasPlayer : styles.noPlayer,
    ].join(' ');
  }, [isMobile, sidebarCollapsed, shouldShowPlayer]);

  // 渲染播放器内容
  const renderPlayer = () => {
    if (title === '音乐') {
      return (
        <Suspense fallback={null}>
          <Player />
        </Suspense>
      );
    }
    return player;
  };

  return (
    <>
      <div className={styles.backgroundContainer}>
        <Suspense fallback={<div className={styles.backgroundFallback} />}>
          {DynamicBackground}
        </Suspense>
      </div>

      <div className={layoutClassName}>
        {!hideHeader && (
          <div className={styles.headerContainer}>
            <Header title={title} actions={headerActions} />
          </div>
        )}

        {!hideSidebar && (
          <div className={styles.sidebarContainer}>
            <Sidebar
              visible={sidebarVisible}
              menuItems={menuItems}
              activeKey={activeKey}
              onMenuClick={onMenuClick}
              collapsed={sidebarCollapsed}
              onToggle={toggleSidebar}
            />
          </div>
        )}

        <main className={`${styles.mainContentArea} ${fullscreenContent ? styles.fullscreenContent : ''}`}>
          {!hidePublicMessage && showPublicMessage && (
            <div className={styles.publicMessageContainer}>
              <PublicMessage onClose={() => setShowPublicMessage(false)} />
            </div>
          )}

          {!hideTabs && (
            <div className={styles.tabsContainer}>
              {tabs}
            </div>
          )}

          <div className={styles.pageContent}>
            {children}
          </div>
        </main>

        {/* 统一播放器位置 - 只渲染一次 */}
        {shouldShowPlayer && (
          <div className={styles.playerContainer}>
            {renderPlayer()}
          </div>
        )}

        {!hideBottomNav && (
          <div className={styles.bottomNavContainer}>
            <BottomNav
              visible={bottomNavVisible}
              menuItems={menuItems}
              activeKey={activeKey}
              onMenuClick={onMenuClick}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default ModuleLayout;